import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSelector, useDispatch } from 'react-redux'
import { WarningIcon, FileDownloadIcon } from '@rsmus/myrsm-icons'
import { toInvoiceSearchFilter } from 'src/utils/helpers/filterAdapter'
import {
  getInvoiceSelectedFilters,
  getInvoiceCounts,
  setExportStatus,
} from 'src/store/invoices/invoiceSelectedFiltersSlice'
import ResponsiveModal from '../../../rsmCoreComponents/components/ResponsiveModal'
import { REACT_APP_EXPORT_MAX_LIMIT } from '../../../envVariables'
import { Styles } from '../../../types'
import api from '../../../api'
import tokens from '../../../styles/tokens.json'
import { useDeviceType } from '../../../rsmCoreComponents/hooks/useDeviceType'

const styles: Styles = {
  DialogTitle: () => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: '0.625rem',
  }),
  ButtonGroup: () => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    paddingTop: '1rem',
    paddingBottom: '1.5rem',
  }),
  warningContainerStyle: (theme) => ({
    width: '100%',
    background: tokens.colors.rsmRed.warning,
    display: 'flex',
    justifyContent: 'center',
    padding: '0.75rem 1rem',
    [theme.breakpoints.only('tablet')]: {
      padding: '0.75rem 5rem 1rem',
    },
  }),
  warningInnerBoxStyle: (theme) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    gap: '0.75rem',
    [theme.breakpoints.only('tablet')]: {
      padding: '0.75rem 3rem 1rem',
      maxWidth: '49rem',
    },
  }),
  warningIconStyle: {
    fontSize: '1.5rem',
    color: tokens.colors.warning.main,
    flexShrink: 0,
  },
  warningTextStyle: {
    color: tokens.colors.rsmText.primary,
    fontWeight: 400,
    lineHeight: 1.5,
    wordBreak: 'break-word',
    flex: 1,
    fontFamily: tokens.type.fontFamilies.preloMedium,
  },
  exportInvoiceHistorylabelStyle: {
    fontWeight: 300,
    textAlign: 'center',
    lineHeight: '2.5rem',
    fontSize: '2.25rem',
  },
  exportRadioGroupStyle: (theme) => ({
    padding: '0.3rem 1rem',
    textAlign: 'center',
    display: 'flex',
    flexflow: 'column',
    flexDirection: 'column',
    alignItems: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.only('desktop')]: {
      padding: '1.3rem 10.9rem',
    },
  }),
  formControlLabelStyle: (theme) => ({
    marginTop: '0.3rem',
    [theme.breakpoints.only('desktop')]: {
      padding: '0.3rem 0rem',
    },
  }),
  formControlLabelEntireStyle: {
    padding: '0.3rem 0em',
    marginBottom: '0.3rem',
  },
}

interface ExportModalProps {
  open: boolean
  onClose: () => void
}

const InvoiceExportModal = ({ open, onClose }: ExportModalProps) => {
  const getDateStamp = (): string => {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const year = String(now.getFullYear()).slice(-2)
    return `${month}-${day}-${year}`
  }
  const DEFAULT_HEIGHT = '25.5rem'
  const ERROR_HEIGHT = '32.5rem'
  const { isDesktop } = useDeviceType()
  const dispatch = useDispatch()
  const [isExportEnabled, setIsExportEnabled] = useState<boolean>(true)
  const [isExporting, setIsExporting] = useState(false)
  const [selectedOption, setSelectedOption] = useState<
    'filtered' | 'entire' | null
  >(null)
  const invoiceFilters = useSelector(getInvoiceSelectedFilters)
  const { filteredInvoices, totalInvoices } = useSelector(getInvoiceCounts)

  const isExportLimitExceeded =
    (selectedOption === 'filtered' &&
      filteredInvoices > REACT_APP_EXPORT_MAX_LIMIT) ||
    (selectedOption === 'entire' && totalInvoices > REACT_APP_EXPORT_MAX_LIMIT)

  const getDesktopMaxHeight = (): string =>
    !selectedOption || isExportLimitExceeded ? ERROR_HEIGHT : DEFAULT_HEIGHT

  useEffect(() => {
    setIsExportEnabled(!!selectedOption && !isExportLimitExceeded)
  }, [selectedOption, filteredInvoices, totalInvoices, isExportLimitExceeded])

  useEffect(() => {
    if (open) {
      if (filteredInvoices > 0 && filteredInvoices !== totalInvoices) {
        setSelectedOption('filtered')
      } else {
        setSelectedOption('entire')
      }
    }
  }, [open, filteredInvoices, totalInvoices])

  const { t } = useTranslation()

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const applyFilter = selectedOption === 'filtered'
      const filterPayload = toInvoiceSearchFilter(invoiceFilters)

      const response = await api.finance.invoiceDocument_ExportInvoicesToCsv(
        applyFilter,
        filterPayload,
      )

      if (response?.data) {
        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Invoices Summary ${getDateStamp()}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()

        dispatch(setExportStatus({ isSuccess: true, showAlert: false }))
        onClose()
      } else {
        onClose()
        setTimeout(() => {
          dispatch(setExportStatus({ isSuccess: false, showAlert: true }))
        }, 100)
      }
    } catch (error) {
      onClose()
      setTimeout(() => {
        dispatch(setExportStatus({ isSuccess: false, showAlert: true }))
        console.error('Export failed:', error)
      }, 100)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      aria-labelledby="export-invoice-history"
      desktopMaxHeight={getDesktopMaxHeight()}
      desktopMaxWidth={isDesktop ? '41rem' : '37rem'}
      removeHeaderSpacing
      removeContentSpacing
      overrideFullScreenDialog
      removeCloseButton
      data-testid="Lbl_Invoicing_InvoiceExport_Modal">
      <Box tabIndex={-1} role="document">
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: 'absolute', top: '0.625rem', right: '0.625rem' }}>
          <CloseIcon sx={{ fontSize: '1.5rem' }} />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '2.5rem',
          }}>
          <FileDownloadIcon
            fontSize="3.5rem"
            width="3.875rem"
            height="3.875rem"
          />
        </Box>

        <Typography
          id="export-radio-group-label"
          variant="h5"
          component="h1"
          role="heading"
          sx={styles.exportInvoiceHistorylabelStyle}>
          {t('Invoicing.InvoiceExport.ExportInvoiceHistory')}
        </Typography>

        <Box sx={styles.exportRadioGroupStyle}>
          <RadioGroup
            value={selectedOption}
            onChange={(e) =>
              setSelectedOption(e.target.value as 'filtered' | 'entire')
            }
            aria-labelledby="export-radio-group-label">
            {filteredInvoices > 0 && filteredInvoices !== totalInvoices ? (
              <>
                <FormControlLabel
                  sx={styles.formControlLabelEntireStyle}
                  data-testid="Radio_InvoiceExportalModal_ExportCSV_Filtered"
                  value="filtered"
                  control={<Radio />}
                  label={
                    <Typography variant="body1">
                      {t('Invoicing.InvoiceExport.ExportFilteredResults')} (
                      {filteredInvoices} results)
                    </Typography>
                  }
                />
                <FormControlLabel
                  sx={styles.formControlLabelStyle}
                  data-testid="Radio_InvoiceExportalModal_ExportCSV_Entire"
                  value="entire"
                  control={<Radio />}
                  label={
                    <Typography variant="body1">
                      {t('Invoicing.InvoiceExport.ExportEntireRecord')} (
                      {totalInvoices} results)
                    </Typography>
                  }
                />
              </>
            ) : (
              <FormControlLabel
                sx={styles.formControlLabelStyle}
                data-testid="Radio_InvoiceExportalModal_ExportCSV_Entire"
                value="entire"
                control={<Radio />}
                label={
                  <Typography variant="body1">
                    {t('Invoicing.InvoiceExport.ExportEntireRecord')} (
                    {totalInvoices} results)
                  </Typography>
                }
              />
            )}
          </RadioGroup>
        </Box>

        {isExportLimitExceeded && (
          <Box
            sx={{
              minHeight: '6.5rem',
              transition: 'all 0.3s ease-in-out',
              opacity: 1,
              visibility: 'visible',
            }}
            role="alert"
            aria-live="assertive"
            aria-atomic="true">
            <Box sx={styles.warningContainerStyle}>
              <Box sx={styles.warningInnerBoxStyle}>
                <WarningIcon sx={styles.warningIconStyle} />
                <Typography variant="body2" sx={styles.warningTextStyle}>
                  {t('Invoicing.InvoiceExport.ExportLimitWarning', {
                    exportLimit: REACT_APP_EXPORT_MAX_LIMIT,
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={styles.ButtonGroup}>
          <Button
            variant="outlined"
            onClick={onClose}
            data-testid="Btn_InvoiceExportalModal_ExportCSV_Cancel">
            {t('Invoicing.InvoiceExport.Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            data-testid="Btn_InvoiceExportalModal_ExportCSV_Export"
            disabled={!isExportEnabled || isExporting}
            startIcon={
              isExporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }>
            {t('Invoicing.InvoiceExport.exportToCSV')}
          </Button>
        </Box>
      </Box>
    </ResponsiveModal>
  )
}

export default InvoiceExportModal
